import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, History, User, Search } from 'lucide-react';

interface GuestSuggestion {
  id: string;
  firstName: string;
  lastName: string;
  documentType: string;
  documentNumber: string;
  phone: string;
  email: string;
  lastStay: string | null;
  personId: string | null;
  matchedBy?: string;
}

interface PersonResult {
  id: string;
  firstName: string;
  lastName: string;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  documentNumber?: string | null;
  totalStays?: number;
}

interface AddViatgerDialogProps {
  reservationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Cercador per afegir un viatger existent a una reserva:
 * - "Estades anteriors" (Guests amb historial): còpia completa, IDESP inclòs (POST /viatgers/copy)
 * - "Persones" (registres Person): vinculació amb totes les dades de contacte (POST /viatgers/from-person)
 */
export function AddViatgerDialog({ reservationId, open, onOpenChange }: AddViatgerDialogProps) {
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();

  const enabled = open && search.trim().length > 2;

  const { data: suggestions = [], isLoading: loadingSuggestions } = useQuery<GuestSuggestion[]>({
    queryKey: ['viatger-suggestions', search],
    queryFn: async () => {
      const res = await api.get('/viatgers/suggestions', { params: { query: search } });
      return res.data;
    },
    enabled,
  });

  const { data: persons = [], isLoading: loadingPersons } = useQuery<PersonResult[]>({
    queryKey: ['persons', 'search', search],
    queryFn: async () => {
      const res = await api.get('/persons/search', { params: { q: search } });
      return res.data;
    },
    enabled,
  });

  const invalidateAndClose = (name: string) => {
    queryClient.invalidateQueries({ queryKey: ['reservation-guests', reservationId] });
    queryClient.invalidateQueries({ queryKey: ['viatgers'] });
    queryClient.invalidateQueries({ queryKey: ['mossos-viatgers'] });
    toast.success(`${name} afegit/da a la reserva`);
    setSearch('');
    onOpenChange(false);
  };

  const onError = (error: any) => {
    toast.error(error?.response?.data?.message || 'No s\'ha pogut afegir el viatger');
  };

  const copyFromGuest = useMutation({
    mutationFn: async (guest: GuestSuggestion) => {
      const res = await api.post('/viatgers/copy', {
        sourceGuestId: guest.id,
        targetReservationId: reservationId,
      });
      return { data: res.data, guest };
    },
    onSuccess: ({ guest }) => invalidateAndClose(`${guest.firstName} ${guest.lastName}`.trim()),
    onError,
  });

  const linkPerson = useMutation({
    mutationFn: async (person: PersonResult) => {
      const res = await api.post('/viatgers/from-person', {
        personId: person.id,
        reservationId,
      });
      return { data: res.data, person };
    },
    onSuccess: ({ person }) => invalidateAndClose(`${person.firstName} ${person.lastName}`.trim()),
    onError,
  });

  const isLoading = loadingSuggestions || loadingPersons;
  const isPending = copyFromGuest.isPending || linkPerson.isPending;

  // Evitar duplicats: si una Person ja apareix com a estada anterior, no repetir-la
  const suggestionPersonIds = new Set(suggestions.map((s) => s.personId).filter(Boolean));
  const filteredPersons = persons.filter((p) => !suggestionPersonIds.has(p.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Afegir viatger</DialogTitle>
          <DialogDescription>
            Cerca per nom, document, email o telèfon entre viatgers d'estades anteriors i persones
            registrades
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Ex: Nom Cognom, 00000000T..."
            className="pl-9"
          />
        </div>

        {enabled && isLoading && (
          <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Cercant...
          </div>
        )}

        {enabled && !isLoading && suggestions.length === 0 && filteredPersons.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6">
            Cap resultat per "{search}"
          </p>
        )}

        {suggestions.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
              <History className="h-3.5 w-3.5" /> Estades anteriors (còpia completa, IDESP inclòs)
            </p>
            {suggestions.map((guest) => (
              <button
                key={guest.id}
                type="button"
                disabled={isPending}
                onClick={() => copyFromGuest.mutate(guest)}
                className="w-full flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors text-left disabled:opacity-50"
              >
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">
                    {guest.firstName} {guest.lastName}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {[guest.documentNumber, guest.phone || guest.email]
                      .filter(Boolean)
                      .join(' · ') || 'Sense document ni contacte'}
                  </p>
                </div>
                {guest.lastStay && (
                  <Badge variant="secondary" className="shrink-0 ml-2 text-xs">
                    {new Date(guest.lastStay).toLocaleDateString('ca-ES')}
                  </Badge>
                )}
              </button>
            ))}
          </div>
        )}

        {filteredPersons.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
              <User className="h-3.5 w-3.5" /> Persones registrades
            </p>
            {filteredPersons.map((person) => (
              <button
                key={person.id}
                type="button"
                disabled={isPending}
                onClick={() => linkPerson.mutate(person)}
                className="w-full flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors text-left disabled:opacity-50"
              >
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">
                    {person.firstName} {person.lastName}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {[person.documentNumber, person.mobile || person.phone, person.email]
                      .filter(Boolean)
                      .join(' · ') || 'Sense dades de contacte'}
                  </p>
                </div>
                {person.totalStays != null && person.totalStays > 0 && (
                  <Badge variant="secondary" className="shrink-0 ml-2 text-xs">
                    {person.totalStays} estades
                  </Badge>
                )}
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
